import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import AIJobsMonitor from "../components/AIJobsMonitor";
import TaxonomyEditor from "../components/TaxonomyEditor";
import PlanManagementDialog from "../components/PlanManagementDialog";
import { Trash2, Users, FileBarChart2, Activity, Globe2, Mail, MailOpen, MessageSquare, Ticket, Copy, Link as LinkIcon, Cpu, CreditCard } from "lucide-react";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [consultants, setConsultants] = useState([]);
  // Pack 10 · plan management modal state
  const [planDialogConsultant, setPlanDialogConsultant] = useState(null);
  const [planPackages, setPlanPackages] = useState({});
  const [leads, setLeads] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [messages, setMessages] = useState([]);
  const [invites, setInvites] = useState([]);
  const [settings, setSettings] = useState(null);
  const [newInvite, setNewInvite] = useState({ kind: "code", plan: "professional", max_uses: 1, note: "" });

  const load = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/consultants").then((r) => setConsultants(r.data));
    api.get("/admin/leads").then((r) => setLeads(r.data));
    api.get("/admin/templates").then((r) => setTemplates(r.data));
    api.get("/admin/messages").then((r) => setMessages(r.data));
    api.get("/admin/invites").then((r) => setInvites(r.data));
    api.get("/admin/settings").then((r) => setSettings(r.data));
    // Pack 10 · plan catalog for the manual billing dialog
    api.get("/billing/plans").then((r) => {
      const map = {};
      const list = Array.isArray(r.data) ? r.data : (r.data?.packages || []);
      list.forEach((p) => {
        const pid = p.package_id || p.id;
        if (pid) map[pid] = p;
      });
      setPlanPackages(map);
    }).catch(() => setPlanPackages({}));
  }, []);

  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Remove this consultant?")) return;
    await api.delete(`/admin/consultants/${id}`);
    toast.success("Removed");
    load();
  };

  const toggleRead = async (m) => {
    await api.patch(`/admin/messages/${m.id}?read=${!m.read}`);
    load();
  };

  const delMessage = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    await api.delete(`/admin/messages/${id}`);
    toast.success("Deleted");
    load();
  };

  const createInvite = async () => {
    try {
      const payload = { ...newInvite, max_uses: Number(newInvite.max_uses) || null };
      const { data } = await api.post("/admin/invites", payload);
      toast.success(data.kind === "magic" ? "Magic link created" : `Code ${data.code} created`);
      setNewInvite({ kind: "code", plan: "professional", max_uses: 1, note: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create invite");
    }
  };

  const revokeInvite = async (inv) => {
    await api.patch(`/admin/invites/${inv.id}`, { revoked: !inv.revoked });
    load();
  };

  const removeInvite = async (id) => {
    if (!window.confirm("Permanently delete this invite?")) return;
    await api.delete(`/admin/invites/${id}`);
    load();
  };

  const toggleSetting = async (key, value) => {
    const { data } = await api.patch("/admin/settings", { [key]: value });
    setSettings(data);
    toast.success("Saved");
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied"); };
  const inviteShareUrl = (inv) => inv.token ? `${window.location.origin}/join/${inv.token}` : "";

  if (!stats) return <div className="p-10 text-muted-foreground">Loading admin…</div>;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="overline"><span className="accent-line" /> Admin control room</div>
      <h1 className="font-display text-4xl mt-3">Platform overview</h1>

      <div className="grid md:grid-cols-4 gap-5 mt-10">
        <Stat icon={Users} label="Consultants" value={stats.total_consultants} testid="admin-stat-consultants" />
        <Stat icon={Activity} label="Discoveries Started" value={stats.total_assessments} testid="admin-stat-started" />
        <Stat icon={FileBarChart2} label="Discoveries Completed" value={stats.completed_assessments} testid="admin-stat-completed" />
        <Stat icon={Globe2} label="Total Leads" value={stats.total_leads} testid="admin-stat-leads" />
      </div>

      <div className="grid lg:grid-cols-3 gap-5 mt-10">
        <PopularCard title="Popular industries" data={stats.popular_industries} testid="admin-popular-industries" />
        <PopularCard title="Popular functions" data={stats.popular_functions} testid="admin-popular-functions" />
        <PopularCard title="Popular countries" data={stats.popular_countries} testid="admin-popular-countries" />
      </div>

      <Tabs defaultValue="consultants" className="mt-12">
        <TabsList className="bg-card/60">
          <TabsTrigger value="consultants" data-testid="admin-tab-consultants">Consultants</TabsTrigger>
          <TabsTrigger value="leads" data-testid="admin-tab-leads">Leads</TabsTrigger>
          <TabsTrigger value="templates" data-testid="admin-tab-templates">Discovery Cache</TabsTrigger>
          <TabsTrigger value="performance" data-testid="admin-tab-performance">Performance</TabsTrigger>
          <TabsTrigger value="ai_jobs" data-testid="admin-tab-ai-jobs">
            <Cpu className="w-3.5 h-3.5 mr-1.5" /> AI Jobs
          </TabsTrigger>
          <TabsTrigger value="messages" data-testid="admin-tab-messages" className="relative">
            Messages
            {stats.unread_messages > 0 && (
              <span className="ml-2 text-[10px] bg-accent text-accent-foreground rounded-full px-1.5 py-0.5 font-semibold" data-testid="admin-unread-badge">
                {stats.unread_messages}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="invites" data-testid="admin-tab-invites">Invites</TabsTrigger>
          <TabsTrigger value="taxonomy" data-testid="admin-tab-taxonomy">Taxonomy</TabsTrigger>
        </TabsList>

        <TabsContent value="consultants" className="mt-6">
          <Card className="card-elevated overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
              <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Company</TableHead><TableHead>Email</TableHead><TableHead>Plan</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {consultants.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell>{c.company}</TableCell>
                    <TableCell>{c.email}</TableCell>
                    <TableCell className="capitalize">{c.plan || "starter"}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => setPlanDialogConsultant(c)}
                        data-testid={`admin-plan-manage-${c.id}`}
                        title="Manage plan"
                      >
                        <CreditCard className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => del(c.id)} data-testid={`admin-del-${c.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="leads" className="mt-6">
          <Card className="card-elevated overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
              <TableHeader><TableRow><TableHead>Company</TableHead><TableHead>Contact</TableHead><TableHead>Country</TableHead><TableHead>Industry</TableHead><TableHead>Function</TableHead><TableHead className="text-right">Score</TableHead><TableHead>Assigned to</TableHead></TableRow></TableHeader>
              <TableBody>
                {leads.map((l) => {
                  const owner = consultants.find((c) => c.id === l.consultant_id);
                  const onReassign = async (newId) => {
                    if (!newId || newId === l.consultant_id) return;
                    await api.patch(`/admin/leads/${l.id}/reassign?new_consultant_id=${newId}`);
                    toast.success("Reassigned");
                    load();
                  };
                  return (
                    <TableRow key={l.id}>
                      <TableCell className="font-medium">{l.company}</TableCell>
                      <TableCell><div>{l.name}</div><div className="text-xs text-muted-foreground">{l.email}</div></TableCell>
                      <TableCell>{l.country}</TableCell>
                      <TableCell>{l.industry}</TableCell>
                      <TableCell>{l.function}</TableCell>
                      <TableCell className="text-right font-display text-xl text-accent">{l.results?.overall_score}</TableCell>
                      <TableCell>
                        <Select value={l.consultant_id || ""} onValueChange={onReassign}>
                          <SelectTrigger className="h-8 min-w-[180px]" data-testid={`reassign-${l.id}`}>
                            <SelectValue placeholder="Select consultant">
                              {owner ? `${owner.name} · ${owner.company || ""}`.trim() : "Unknown"}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {consultants.map((c) => (
                              <SelectItem key={c.id} value={c.id}>{c.name} · {c.company || ""}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="templates" className="mt-6">
          <Card className="card-elevated overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
              <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Country</TableHead><TableHead>Industry</TableHead><TableHead>Function</TableHead><TableHead className="text-right">Questions</TableHead></TableRow></TableHeader>
              <TableBody>
                {templates.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="font-medium">{t.title}</TableCell>
                    <TableCell>{t.country}</TableCell>
                    <TableCell>{t.industry}</TableCell>
                    <TableCell>{t.function}</TableCell>
                    <TableCell className="text-right">{t.questions?.length || 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="performance" className="mt-6">
          <Card className="card-elevated overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
              <TableHeader><TableRow><TableHead>Consultant</TableHead><TableHead>Company</TableHead><TableHead>Email</TableHead><TableHead className="text-right">Leads</TableHead></TableRow></TableHeader>
              <TableBody>
                {stats.consultant_performance.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>{c.name}</TableCell>
                    <TableCell>{c.company}</TableCell>
                    <TableCell>{c.email}</TableCell>
                    <TableCell className="text-right font-display text-xl text-accent">{c.leads}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="ai_jobs" className="mt-6">
          <AIJobsMonitor />
        </TabsContent>

        <TabsContent value="messages" className="mt-6">
          <Card className="card-elevated p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline">Contact form submissions</div>
                <h3 className="font-sans-ui font-semibold text-lg mt-1">Messages from the website</h3>
              </div>
              <div className="text-xs text-muted-foreground">{messages.length} total · {messages.filter(m => !m.read).length} unread</div>
            </div>
            {messages.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <MessageSquare className="w-8 h-8 mx-auto opacity-60" />
                <p className="mt-3 text-sm">No messages yet.</p>
              </div>
            )}
            <div className="space-y-3">
              {messages.map((m) => (
                <Card key={m.id} className={`p-5 border transition-colors ${m.read ? "border-border/60 bg-card/60" : "border-accent/40 bg-card"}`} data-testid={`admin-msg-${m.id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-sans-ui font-semibold">{m.name}</span>
                        {m.company && <span className="text-muted-foreground text-sm">· {m.company}</span>}
                        {!m.read && <span className="text-[10px] uppercase tracking-widest bg-accent/20 text-accent rounded-full px-2 py-0.5">New</span>}
                      </div>
                      <a href={`mailto:${m.email}`} className="text-sm text-accent hover:underline mt-1 inline-block break-all">{m.email}</a>
                      <p className="text-sm mt-3 leading-relaxed whitespace-pre-wrap break-words">{m.message}</p>
                      <div className="text-xs text-muted-foreground mt-3">{new Date(m.created_at).toLocaleString()}</div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => toggleRead(m)} data-testid={`admin-msg-toggle-${m.id}`} title={m.read ? "Mark as unread" : "Mark as read"}>
                        {m.read ? <Mail className="w-4 h-4" /> : <MailOpen className="w-4 h-4" />}
                      </Button>
                      <a href={`mailto:${m.email}?subject=Re: BizPulse AI inquiry`} title="Reply by email">
                        <Button variant="outline" size="sm" data-testid={`admin-msg-reply-${m.id}`}>Reply</Button>
                      </a>
                      <Button variant="ghost" size="sm" onClick={() => delMessage(m.id)} data-testid={`admin-msg-del-${m.id}`} title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="invites" className="mt-6 space-y-6">
          {/* Kill switch + settings */}
          <Card className="card-elevated p-6">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <Ticket className="w-5 h-5 text-accent" />
                <div>
                  <div className="overline">Beta access controls</div>
                  <h3 className="font-sans-ui font-semibold text-lg mt-1">Invites & kill switch</h3>
                </div>
              </div>
              {settings && (
                <div className="flex items-center gap-6 flex-wrap">
                  <div className="flex items-center gap-3" data-testid="setting-invites-enabled">
                    <Switch checked={!!settings.invites_enabled}
                      onCheckedChange={(v) => toggleSetting("invites_enabled", v)} />
                    <div>
                      <div className="text-sm font-medium">Invites enabled</div>
                      <div className="text-xs text-muted-foreground">Kill switch — flip off to instantly disable all codes & links.</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3" data-testid="setting-invites-required">
                    <Switch checked={!!settings.invite_required_on_register}
                      onCheckedChange={(v) => toggleSetting("invite_required_on_register", v)} />
                    <div>
                      <div className="text-sm font-medium">Closed beta</div>
                      <div className="text-xs text-muted-foreground">Require an invite to register.</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Create invite */}
          <Card className="card-elevated p-6">
            <div className="overline mb-3">New invite</div>
            <div className="grid md:grid-cols-5 gap-4 items-end">
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Type</Label>
                <Select value={newInvite.kind} onValueChange={(v) => setNewInvite({...newInvite, kind: v})}>
                  <SelectTrigger className="mt-2" data-testid="new-invite-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="code">Typed code</SelectItem>
                    <SelectItem value="magic">Magic link</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Plan</Label>
                <Select value={newInvite.plan} onValueChange={(v) => setNewInvite({...newInvite, plan: v})}>
                  <SelectTrigger className="mt-2" data-testid="new-invite-plan"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="starter">Starter</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Max uses (0 = ∞)</Label>
                <Input type="number" min={0} value={newInvite.max_uses}
                  onChange={(e) => setNewInvite({...newInvite, max_uses: e.target.value})}
                  className="mt-2" data-testid="new-invite-uses" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Note (who / why)</Label>
                <Input value={newInvite.note}
                  onChange={(e) => setNewInvite({...newInvite, note: e.target.value})}
                  className="mt-2" placeholder="e.g. Sara Khan – CFO Roundtable" data-testid="new-invite-note" />
              </div>
              <div className="md:col-span-5 flex justify-end">
                <Button onClick={createInvite} data-testid="new-invite-create" className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-6">
                  Create invite
                </Button>
              </div>
            </div>
          </Card>

          {/* Invite list */}
          <Card className="card-elevated overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Code / Link</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Uses</TableHead>
                  <TableHead>Note</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {invites.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">No invites yet. Create one above.</TableCell></TableRow>
                  )}
                  {invites.map((inv) => (
                    <TableRow key={inv.id} className={inv.revoked ? "opacity-50" : ""} data-testid={`invite-row-${inv.id}`}>
                      <TableCell className="max-w-[320px]">
                        {inv.kind === "code" ? (
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm">{inv.code}</span>
                            <Button variant="ghost" size="sm" onClick={() => copy(inv.code)} data-testid={`invite-copy-${inv.id}`}><Copy className="w-3.5 h-3.5" /></Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 min-w-0">
                            <LinkIcon className="w-3.5 h-3.5 text-accent shrink-0" />
                            <span className="font-mono text-xs break-all min-w-0">{inviteShareUrl(inv)}</span>
                            <Button variant="ghost" size="sm" onClick={() => copy(inviteShareUrl(inv))} data-testid={`invite-copy-${inv.id}`}><Copy className="w-3.5 h-3.5" /></Button>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="capitalize">{inv.plan}</TableCell>
                      <TableCell>{inv.uses}{inv.max_uses ? ` / ${inv.max_uses}` : " / ∞"}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{inv.note || "—"}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button variant="ghost" size="sm" onClick={() => revokeInvite(inv)} data-testid={`invite-revoke-${inv.id}`}>
                          {inv.revoked ? "Re-enable" : "Revoke"}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => removeInvite(inv.id)} data-testid={`invite-del-${inv.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="taxonomy" className="mt-6">
          <TaxonomyEditor />
        </TabsContent>
      </Tabs>

      {/* Pack 10 · Plan Management dialog */}
      <PlanManagementDialog
        consultant={planDialogConsultant}
        open={!!planDialogConsultant}
        onOpenChange={(o) => !o && setPlanDialogConsultant(null)}
        packages={planPackages}
      />
    </div>
  );
}

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <Card className="card-elevated p-5" data-testid={testid}>
      <div className="flex items-center justify-between"><div className="overline">{label}</div><Icon className="w-4 h-4 text-accent" /></div>
      <div className="font-display text-4xl mt-3">{value}</div>
    </Card>
  );
}

function PopularCard({ title, data, testid }) {
  return (
    <Card className="card-elevated p-6" data-testid={testid}>
      <div className="overline">{title}</div>
      <div className="mt-4 space-y-3">
        {data.length === 0 && <div className="text-sm text-muted-foreground">No data yet.</div>}
        {data.map(([name, count], i) => (
          <div key={name}>
            <div className="flex items-center justify-between text-sm"><span>{i + 1}. {name}</span><span className="text-muted-foreground">{count}</span></div>
            <div className="h-1.5 bg-secondary mt-2 rounded-full overflow-hidden"><div className="h-full bg-accent" style={{ width: `${Math.min(100, count * 25)}%` }} /></div>
          </div>
        ))}
      </div>
    </Card>
  );
}
